import type { Settings } from '@ais-ui/websockets';
import type { SettingsDomain } from './types';
import { coreTranslator } from './translators/core';
import { mapTranslator } from './translators/map';
import { vesselsTranslator } from './translators/vessels';
import { messagesTranslator } from './translators/messages';
import { wmsTranslator } from './translators/wms';
import { vectorTilesTranslator } from './translators/vectorTiles';
import { radarOverlaysTranslator } from './translators/radarOverlays';

/**
 * Declarative registry of the domains the login-time import owns. This is the
 * single source of truth for "what gets seeded from the blob into the service".
 * The reserved `_import` meta-domain is intentionally NOT listed here — it is the
 * idempotency guard, never a seed target.
 *
 * Each entry exposes only a type-erased `fromBlob` (the seed payload); the
 * orchestrator binds the matching service write op (PATCH document / POST items)
 * by domain name, keeping the mutation hooks out of this plain data module.
 */
export interface OwnedDocumentDomain {
  name: Extract<SettingsDomain, 'core' | 'map' | 'vessels' | 'messages'>;
  kind: 'document';
  fromBlob: (blob: Settings) => Record<string, unknown>;
}

export interface OwnedCollectionDomain {
  name: Extract<SettingsDomain, 'wms' | 'vector_tiles' | 'radar_overlays'>;
  kind: 'collection';
  collectionPath: string;
  fromBlob: (blob: Settings) => { id: string }[];
}

export type OwnedDomain = OwnedDocumentDomain | OwnedCollectionDomain;

export const OWNED_DOCUMENT_DOMAINS: OwnedDocumentDomain[] = [
  {
    name: 'core',
    kind: 'document',
    fromBlob: (blob) =>
      coreTranslator.fromBlob(blob) as unknown as Record<string, unknown>,
  },
  {
    name: 'map',
    kind: 'document',
    fromBlob: (blob) =>
      mapTranslator.fromBlob(blob) as unknown as Record<string, unknown>,
  },
  {
    name: 'vessels',
    kind: 'document',
    fromBlob: (blob) =>
      vesselsTranslator.fromBlob(blob) as unknown as Record<string, unknown>,
  },
  {
    name: 'messages',
    kind: 'document',
    fromBlob: (blob) =>
      messagesTranslator.fromBlob(blob) as unknown as Record<string, unknown>,
  },
];

export const OWNED_COLLECTION_DOMAINS: OwnedCollectionDomain[] = [
  {
    name: 'wms',
    kind: 'collection',
    collectionPath: 'services',
    fromBlob: (blob) => wmsTranslator.fromBlob(blob),
  },
  {
    name: 'vector_tiles',
    kind: 'collection',
    collectionPath: 'tiles',
    fromBlob: (blob) => vectorTilesTranslator.fromBlob(blob),
  },
  {
    name: 'radar_overlays',
    kind: 'collection',
    collectionPath: 'overlays',
    fromBlob: (blob) => radarOverlaysTranslator.fromBlob(blob),
  },
];
